// PersonasView.jsx
//
// Behavioral personas tab. Personas are model-scoped, so the view has
// a model picker; switching models reloads the persona list. Each row
// has Edit + Delete actions; "+ New Persona" opens the form modal.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, CircularProgress, MenuItem, Select, Snackbar, Typography,
} from "@mui/material";

import {
  getForecastModels, getBehavioralPersonas, deleteBehavioralPersona,
} from "../../services/forecast/forecastservices";
import PersonaFormModal from "./modals/PersonaFormModal";
import {
  BLUE, GREEN, RED, ActionButton, EmptyState,
} from "./forecastShared";


// ── Rule summarizer ────────────────────────────────────────────────
// Detection rules come in two shapes:
//
//   Schema A - signal-based (used by sp_recompute_persona_assignments):
//     { signal: "pct_payments_on_friday", threshold: 0.4, comparison: "gte" }
//   Schema B - typed (legacy / handwritten rules):
//     { type: "pays_on_dow", dow: 5 }
//     { type: "pays_near_day_of_month", day: 1, window: 3 }
//
// We render both as compact human-readable text. Unrecognized shapes
// fall back to a truncated JSON string so the cell never blows up the
// column width.
const SIGNAL_LABELS = {
  pct_payments_on_friday:          "Pays on Friday",
  pct_payments_near_1st:           "Near 1st of month",
  pct_payments_near_15th:          "Near 15th of month",
  pct_payments_near_last_bday:     "Near last business day",
  pct_payments_same_day_as_promise:"Same day as promise",
  pct_payments_24hr_of_promise:    "Within 24hr of promise",
  pct_promises_7_plus_days:        "Promises 7+ days out",
};

const COMP_LABELS = { gte: "≥", lte: "≤", eq: "=" };

function summarizeRule(rule) {
  if (!rule) return "—";
  try {
    const cfg = typeof rule === "string" ? JSON.parse(rule) : rule;
    const dows = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];

    // Schema A: signal-based
    if (cfg.signal) {
      const label = SIGNAL_LABELS[cfg.signal] || cfg.signal;
      const comp  = COMP_LABELS[cfg.comparison] || cfg.comparison || "=";
      const thr   = cfg.threshold != null
        ? `${(Number(cfg.threshold) * 100).toFixed(0)}%`
        : "?";
      return `${label} ${comp} ${thr}`;
    }

    // Schema B: typed
    if (cfg.type === "pays_on_dow")              return `Pays on ${dows[cfg.dow] || `dow ${cfg.dow}`}`;
    if (cfg.type === "pays_near_day_of_month") {
      const win = cfg.window != null ? ` (±${cfg.window}d)` : "";
      return `Pays near day ${cfg.day}${win}`;
    }
    if (cfg.type === "same_day_payment")          return "Same-day payer";
    if (cfg.type === "pays_near_holiday")         return `Pays near holiday (±${cfg.window || 5}d)`;

    // Unknown shape - render compact JSON, but cap length so the cell
    // can't smash the layout.
    const json = JSON.stringify(cfg);
    return json.length > 60 ? json.slice(0, 57) + "…" : json;
  } catch {
    const s = String(rule);
    return s.length > 60 ? s.slice(0, 57) + "…" : s;
  }
}


// ── Layout ─────────────────────────────────────────────────────────
const COLS = [
  { key: "name",        label: "Name",         align: "left",  width: "1.5fr" },
  { key: "description", label: "Description",  align: "left",  width: "2.4fr" },
  { key: "rule_summary",label: "Detection",    align: "left",  width: "2fr",   type: "rule"    },
  { key: "is_active",   label: "Status",       align: "left",  width: "0.7fr", type: "status"  },
  { key: "actions",     label: "",             align: "right", width: "1.2fr", type: "actions" },
];
const GRID = COLS.map(c => c.width).join(" ");


export default function PersonasView() {
  const [modelId,    setModelId]    = useState("");
  const [models,     setModels]     = useState([]);
  const [personas,   setPersonas]   = useState([]);
  const [loadingModels,   setLoadingModels]   = useState(true);
  const [loadingPersonas, setLoadingPersonas] = useState(false);
  const [error,      setError]      = useState("");

  // Modal state
  const [personaModal, setPersonaModal] = useState({ open: false, mode: "create", row: null });
  const [toast,        setToast]        = useState({ open: false, message: "", severity: "success" });

  // ── Load models ────────────────────────────────────────────────
  useEffect(() => {
    setLoadingModels(true);
    getForecastModels()
      .then(data => {
        const list = (data || []).filter(m => m.is_active);
        setModels(list);
        if (list.length > 0) setModelId(list[0].id);
      })
      .catch(e => {
        console.error("Failed to load models:", e);
        setError("Failed to load models");
      })
      .finally(() => setLoadingModels(false));
  }, []);

  const selectedModel = useMemo(
    () => models.find(m => m.id === modelId),
    [models, modelId]
  );

  // ── Load personas for selected model ───────────────────────────
  const loadPersonas = useCallback(async () => {
    if (!modelId) { setPersonas([]); return; }
    setLoadingPersonas(true); setError("");
    try {
      const data = await getBehavioralPersonas(modelId);
      setPersonas(data || []);
    } catch (e) {
      setError(`Failed to load personas: ${e?.response?.data?.error || e?.message}`);
    } finally {
      setLoadingPersonas(false);
    }
  }, [modelId]);

  useEffect(() => { loadPersonas(); }, [loadPersonas]);

  // ── Action handlers ────────────────────────────────────────────
  const handleAdd  = ()    => setPersonaModal({ open: true, mode: "create", row: null });
  const handleEdit = (row) => setPersonaModal({ open: true, mode: "edit",   row });

  const handleDelete = async (row) => {
    const ok = window.confirm(`Delete the persona "${row.name}"?\n\nThis soft-deletes it (sets is_active = 0). Historical assignments remain for traceability.`);
    if (!ok) return;
    try {
      await deleteBehavioralPersona(row.id);
      setToast({ open: true, message: `Deleted "${row.name}"`, severity: "success" });
      loadPersonas();
    } catch (e) {
      setToast({ open: true, message: `Delete failed: ${e?.response?.data?.error || e?.message}`, severity: "error" });
    }
  };

  const handleSaved = (mode) => {
    setToast({
      open: true,
      message: mode === "create" ? "Persona created" : "Persona updated",
      severity: "success",
    });
    loadPersonas();
  };

  // ── Cell renderers ─────────────────────────────────────────────
  const renderCell = (col, row) => {
    if (col.type === "rule") return (
      <Box
        component="span"
        title={typeof row.detection_rule === "string"
          ? row.detection_rule
          : JSON.stringify(row.detection_rule)}
        sx={{
          color: "text.secondary", fontSize: 12,
          // Clamp to column - the parent cell already has minWidth:0
          // (grid-cell default would be auto / content-driven), so we
          // also need an explicit width:100% here.
          width: "100%",
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          display: "block",
        }}
      >
        {summarizeRule(row.detection_rule)}
      </Box>
    );
    if (col.type === "status") return (
      <Box component="span" sx={{
        display: "inline-block", px: "7px", py: "1px",
        borderRadius: "20px", fontSize: 11, fontWeight: 500,
        bgcolor: row.is_active ? `${GREEN}18` : "#88888818",
        color:   row.is_active ? GREEN : "#888",
        border:  `0.5px solid ${row.is_active ? `${GREEN}44` : "#88888844"}`,
      }}>
        {row.is_active ? "Active" : "Inactive"}
      </Box>
    );
    if (col.type === "actions") return (
      <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
        <Box
          onClick={() => handleEdit(row)}
          sx={{
            px: "10px", py: "3px", fontSize: 11, borderRadius: "20px",
            cursor: "pointer", userSelect: "none",
            border: "0.5px solid", borderColor: BLUE, color: BLUE, fontWeight: 500,
            transition: "all .15s",
            "&:hover": { background: `${BLUE}18` },
          }}
        >Edit</Box>
        <Box
          onClick={() => handleDelete(row)}
          sx={{
            px: "10px", py: "3px", fontSize: 11, borderRadius: "20px",
            cursor: "pointer", userSelect: "none",
            border: "0.5px solid", borderColor: RED, color: RED, fontWeight: 500,
            transition: "all .15s",
            "&:hover": { background: `${RED}18` },
          }}
        >Delete</Box>
      </Box>
    );
    if (col.key === "name") return (
      <Box sx={{ fontWeight: 500, color: "text.primary" }}>{row[col.key]}</Box>
    );
    if (col.key === "description") {
      const text = row[col.key] || "";
      return (
        <Box sx={{
          color: text ? "text.secondary" : "text.disabled", fontSize: 12,
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {text || "—"}
        </Box>
      );
    }
    return <Box component="span">{row[col.key]}</Box>;
  };

  return (
    <Box sx={{ p: 2 }}>

      {/* ── Header with model picker + add button ── */}
      <Box sx={{
        display: "flex", alignItems: "flex-end", gap: 2,
        pb: 2, mb: 1, borderBottom: "0.5px solid", borderColor: "divider",
      }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography sx={{ fontSize: 11, color: "text.secondary",
            textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Model
          </Typography>
          <Select
            value={modelId}
            onChange={e => setModelId(e.target.value)}
            size="small" variant="outlined"
            disabled={loadingModels || models.length === 0}
            displayEmpty
            sx={{
              fontSize: 12, borderRadius: "20px", minWidth: 200, height: 28,
              ".MuiOutlinedInput-notchedOutline": { borderColor: "divider", borderWidth: "0.5px" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BLUE },
              ".MuiSelect-select": { py: "3px", px: "10px", fontSize: 12, color: "text.secondary" },
              ".MuiSvgIcon-root": { fontSize: 16, color: "text.secondary" },
            }}
          >
            {models.length === 0 && (
              <MenuItem value="" disabled sx={{ fontSize: 12 }}>
                {loadingModels ? "Loading…" : "No models yet"}
              </MenuItem>
            )}
            {models.map(m => (
              <MenuItem key={m.id} value={m.id} sx={{ fontSize: 12 }}>
                {m.name}
              </MenuItem>
            ))}
          </Select>
        </Box>

        <Box sx={{ ml: "auto", alignSelf: "flex-end" }}>
          <ActionButton
            onClick={handleAdd}
            icon="+"
            color={BLUE}
            disabled={!modelId}
          >
            New Persona
          </ActionButton>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {(loadingModels || loadingPersonas) && (
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 2 }}>
          <CircularProgress size={20} /> Loading…
        </Box>
      )}

      {!loadingModels && models.length === 0 && (
        <EmptyState
          title="No models yet"
          hint="Create a model first on the Models tab. Behavioral personas are scoped per model."
        />
      )}

      {!loadingModels && selectedModel && !loadingPersonas && (
        <>
          <Box sx={{ mb: 2, fontSize: 12, color: "text.secondary" }}>
            <Box component="span" sx={{ color: "text.primary", fontWeight: 500 }}>
              {selectedModel.name}
            </Box>
            {selectedModel.description ? ` · ${selectedModel.description}` : ""}
          </Box>

          {personas.length === 0 ? (
            <EmptyState
              title="No behavioral personas defined yet"
              hint='Click "+ New Persona" to define one. Examples: "Pays on Friday", "Pays near the 15th", "Same-day payer".'
            />
          ) : (
            <Box sx={{
              border: "0.5px solid", borderColor: "divider",
              borderRadius: 2, overflow: "hidden",
            }}>
              <Box sx={{
                display: "grid", gridTemplateColumns: GRID, px: 2, py: 1,
                background: "#f7f7f7", borderBottom: "0.5px solid", borderColor: "divider",
                fontSize: 11, fontWeight: 500,
                textTransform: "uppercase", letterSpacing: "0.05em",
              }}>
                {COLS.map(col => (
                  <Box key={col.key} sx={{ textAlign: col.align, color: "text.secondary" }}>
                    {col.label}
                  </Box>
                ))}
              </Box>
              {personas.map((row, i) => (
                <Box key={row.id} sx={{
                  display: "grid", gridTemplateColumns: GRID, px: 2, py: "10px",
                  fontSize: 12, borderBottom: "0.5px solid", borderColor: "divider",
                  background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
                  "&:hover": { background: "rgba(55,138,221,0.06)" },
                  "&:last-of-type": { borderBottom: "none" },
                }}>
                  {COLS.map(col => (
                    <Box key={col.key} sx={{
                      display: "flex", alignItems: "center", minWidth: 0,
                      justifyContent: col.align === "right" ? "flex-end" : "flex-start",
                    }}>
                      {renderCell(col, row)}
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          )}
        </>
      )}

      {/* ── Modals ── */}
      <PersonaFormModal
        open={personaModal.open}
        mode={personaModal.mode}
        modelId={modelId}
        initialData={personaModal.row}
        onClose={() => setPersonaModal({ ...personaModal, open: false })}
        onSaved={() => handleSaved(personaModal.mode)}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToast({ ...toast, open: false })}
          severity={toast.severity}
          sx={{ fontSize: 13 }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}